import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useNavigate, NavigateFunction } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  Chip,
  Stack,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PaymentIcon from "@mui/icons-material/Payment";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { useGetInvoiceById } from "../../hooks/api/useInvoice";
import { useGetOrder } from "../../hooks/api/useSalesOrder";
import { useGetCustomerList } from "../../hooks/api/useCustomer";
import { usePrintOrderLineDisplayNames } from "../../hooks/usePrintOrderLineDisplayNames";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  PORTAL_PREFIX,
  PAGE_PORTAL_ORDERS_SALES_DETAIL,
  PAGE_PORTAL_ORDERS_PAYMENT_DETAIL,
  KivuI18nContext,
  localizedPath,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import PrintButton from "../common/PrintButton/PrintButton";
import PrintableContent from "../common/PrintableContent/PrintableContent";
import CommonLoader from "../common/CommonLoader/CommonLoader";
import {
  formatDate,
  formatDateTime,
  getPaymentStatusColor
} from "../Orders/utils/orderUtils";
import { InvoiceStatus } from "./enums/invoice";
import { PaymentMethodLabels } from "../Payment/enums/paymentMethod";
import CreatePaymentModal from "../Payment/CreatePaymentModal";
import { useReportError } from "../../hooks/useReportError";
import { useFormatCurrency } from "../../hooks/useFormatCurrency";
import OpaqueIdOrText from "../common/OpaqueIdOrText";
import InvoicePrintLayout from "../PrintDocuments/InvoicePrintLayout";
import {
  buildInvoicePrintLayoutProps,
  resolvePrintStoreId
} from "../PrintDocuments/printDocumentMappers";
import type { Order } from "../Orders/types/salesOrder";
export interface InvoiceDetailModalProps {
  open: boolean;
  invoiceId: string | null;
  onClose: () => void;
}

const InvoiceDetailModal: React.FC<InvoiceDetailModalProps> = ({
  open,
  invoiceId,
  onClose
}) => {
  const formatCurrency = useFormatCurrency();
  const navigate = useNavigate();
  const navigateAndClose = useCallback<NavigateFunction>(
    (to, options) => {
      onClose();
      navigate(to, options);
    },
    [onClose, navigate]
  );
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};
  const reportError = useReportError();

  const { invoiceDetail, invoiceList } = useSelector(
    (state: RootState) => state.invoices
  );
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const companyName = useSelector(
    (state: RootState) => state.company.companyName
  );
  const storeList = useSelector((state: RootState) => state.store.storeList);
  const customerList = useSelector(
    (state: RootState) => state.customers.customerList
  );
  const { mutateAsync: fetchInvoiceById, isPending } = useGetInvoiceById();
  const { mutateAsync: fetchOrderById } = useGetOrder();
  useGetCustomerList(userDetails?.companyId ?? undefined);
  const printContentRef = useRef<HTMLDivElement>(null);
  const [openCreatePayment, setOpenCreatePayment] = useState(false);
  const [orderForPrint, setOrderForPrint] = useState<Order | null>(null);
  const { displayNameByItemId } = usePrintOrderLineDisplayNames(orderForPrint);

  useEffect(() => {
    if (!open || !invoiceId) return;
    (async () => {
      try {
        await fetchInvoiceById({ invoiceId });
      } catch (err) {
        reportError("Failed to fetch invoice detail (UI)", err, {
          component: "InvoiceDetailModal",
          invoiceId: invoiceId ?? ""
        });
      }
    })();
  }, [open, invoiceId, fetchInvoiceById, reportError]);

  useEffect(() => {
    if (!open) setOpenCreatePayment(false);
  }, [open]);

  const invoice = useMemo(() => {
    if (!invoiceId) return undefined;
    if (invoiceDetail && invoiceDetail.invoiceId === invoiceId) {
      return invoiceDetail;
    }
    return invoiceList.find((inv) => inv.invoiceId === invoiceId);
  }, [invoiceDetail, invoiceId, invoiceList]);

  const invoiceMatches =
    !!invoice && invoiceId !== null && invoice.invoiceId === invoiceId;

  useEffect(() => {
    if (!open || !invoiceMatches || !invoice?.orderId) {
      setOrderForPrint(null);
      return;
    }
    let cancelled = false;
    setOrderForPrint(null);
    (async () => {
      try {
        const order = await fetchOrderById({ orderId: invoice.orderId });
        if (!cancelled) setOrderForPrint(order);
      } catch (err) {
        reportError("Failed to fetch order for invoice print (modal)", err, {
          component: "InvoiceDetailModal",
          orderId: invoice.orderId
        });
        if (!cancelled) setOrderForPrint(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, invoiceMatches, invoice, fetchOrderById, reportError]);

  const invoicePrintProps = useMemo(() => {
    if (!invoice || !invoiceId || invoice.invoiceId !== invoiceId) return null;
    const customer = invoice.customerId
      ? customerList.find((c) => c.id === invoice.customerId)
      : undefined;
    const storeId = resolvePrintStoreId(orderForPrint, invoice);
    return buildInvoicePrintLayoutProps(
      invoice,
      orderForPrint,
      companyName,
      storeList.find((s) => s.id === storeId),
      customer,
      displayNameByItemId,
      t
    );
  }, [
    invoice,
    invoiceId,
    orderForPrint,
    companyName,
    storeList,
    customerList,
    displayNameByItemId,
    t
  ]);

  const invoiceForActions =
    invoiceDetail?.invoiceId === invoiceId ? invoiceDetail : undefined;

  const canRecordPayment =
    invoiceForActions &&
    (invoiceForActions.status === InvoiceStatus.UNPAID ||
      invoiceForActions.status === InvoiceStatus.PARTIAL_PAID);

  const salesOrderDetailPath =
    invoice && invoice.orderId
      ? localizedPath(
          `/${PORTAL_PREFIX}/${PAGE_PORTAL_ORDERS_SALES_DETAIL.replace(
            ":orderId",
            invoice.orderId
          )}`,
          language,
          defaultLang
        )
      : "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      aria-labelledby="invoice-detail-modal-title"
    >
      <DialogTitle
        id="invoice-detail-modal-title"
        aria-label={t("invoice-details-title", "Invoice details")}
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          pr: 1,
          pb: 1
        }}
      >
        <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
          <Typography variant="h6" component="span" fontWeight="bold">
            {t("invoice-details-title", "Invoice details")}
          </Typography>
          {invoice && (
            <Chip
              size="small"
              sx={{ ml: 1, verticalAlign: "middle" }}
              label={invoice.status}
              color={getPaymentStatusColor(invoice.status)}
            />
          )}
        </Box>
        <Box display="flex" alignItems="center" gap={1} flexShrink={0}>
          {canRecordPayment && invoiceForActions && (
            <Button
              size="small"
              variant="contained"
              startIcon={<PaymentIcon />}
              onClick={() => setOpenCreatePayment(true)}
              sx={{
                backgroundColor: BRAND_COLOR,
                "&:hover": { backgroundColor: BRAND_HOVER },
                textTransform: "none"
              }}
            >
              {t("record-payment", "Record Payment")}
            </Button>
          )}
          {invoice && (
            <PrintButton
              targetRef={printContentRef}
              title={`${t("invoice", "Invoice")}: ${invoice.invoiceNumber}`}
              label={t("print-invoice", "Print Invoice")}
            />
          )}
          <IconButton
            aria-label={t("close", "Close")}
            onClick={onClose}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 0 }}>
        {open && invoiceId && isPending && (
          <CommonLoader
            loadingMessage={t(
              "fetching-invoice-details",
              "Fetching invoice details..."
            )}
          />
        )}

        {open && invoiceId && !isPending && !invoiceMatches && (
          <Typography color="text.secondary">
            {t("invoice-not-found", "Invoice could not be loaded.")}
          </Typography>
        )}

        {open && invoiceId && !isPending && invoiceMatches && invoice && (
          <PrintableContent
            id="invoice-printable-content-modal"
            ref={printContentRef}
          >
            <Box sx={{ display: "none", "@media print": { display: "block" } }}>
              {invoicePrintProps ? (
                <InvoicePrintLayout {...invoicePrintProps} />
              ) : null}
            </Box>

            <Box sx={{ "@media print": { display: "none !important" } }}>
              <Paper sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      {invoice.invoiceNumber}
                    </Typography>
                  </Box>

                  <Divider />

                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      {t("order-number", "Order Number")}:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {invoice.orderNumber}
                    </Typography>
                  </Box>

                  {invoice.orderId && salesOrderDetailPath && (
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      gap={1}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {t("order", "Order")}:
                      </Typography>
                      <Typography
                        component="a"
                        href={salesOrderDetailPath}
                        onClick={(e) => {
                          e.preventDefault();
                          navigateAndClose(salesOrderDetailPath);
                        }}
                        variant="body2"
                        fontWeight="medium"
                        sx={{
                          color: BRAND_COLOR,
                          textDecoration: "underline",
                          textUnderlineOffset: 2,
                          textAlign: "right",
                          "&:hover": { color: BRAND_HOVER }
                        }}
                      >
                        {t("view-order", "View Order")}
                      </Typography>
                    </Box>
                  )}

                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      {t("customer", "Customer")}:
                    </Typography>
                    <OpaqueIdOrText value={invoice.customerId} />
                  </Box>

                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      {t("issue-date", "Issue Date")}:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatDate(invoice.issueDate)}
                    </Typography>
                  </Box>

                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      {t("created", "Created")}:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatDateTime(invoice.createdAt)}
                    </Typography>
                  </Box>

                  {(invoice.payments?.length ?? 0) > 0 && (
                    <>
                      <Divider />
                      <Typography variant="subtitle2" fontWeight="bold">
                        {t("payments", "Payments")} ({invoice.payments.length})
                      </Typography>
                      <Stack spacing={1.5}>
                        {(invoice.payments ?? []).map((payment) => (
                          <Box
                            key={payment.paymentId}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{
                              py: 1,
                              px: 1.5,
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1
                            }}
                          >
                            <Box
                              display="flex"
                              flexDirection="column"
                              gap={0.5}
                            >
                              <Typography
                                component="a"
                                href={localizedPath(
                                  `/${PORTAL_PREFIX}/${PAGE_PORTAL_ORDERS_PAYMENT_DETAIL.replace(
                                    ":paymentId",
                                    payment.paymentId
                                  )}`,
                                  language,
                                  defaultLang
                                )}
                                onClick={(e) => {
                                  e.preventDefault();
                                  navigateAndClose(
                                    localizedPath(
                                      `/${PORTAL_PREFIX}/${PAGE_PORTAL_ORDERS_PAYMENT_DETAIL.replace(
                                        ":paymentId",
                                        payment.paymentId
                                      )}`,
                                      language,
                                      defaultLang
                                    )
                                  );
                                }}
                                variant="body2"
                                fontWeight="medium"
                                sx={{
                                  color: BRAND_COLOR,
                                  textDecoration: "none",
                                  cursor: "pointer",
                                  "&:hover": { textDecoration: "underline" }
                                }}
                              >
                                {t("view-payment", "View Payment")}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {formatDate(payment.paymentDate)} •{" "}
                                {PaymentMethodLabels[payment.paymentMethod] ||
                                  payment.paymentMethod}
                                {payment.reference && ` • ${payment.reference}`}
                              </Typography>
                            </Box>
                            <Typography variant="body2" fontWeight="bold">
                              {formatCurrency(payment.amount)}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </>
                  )}

                  <Divider />

                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      {t("subtotal", "Subtotal")}:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatCurrency(invoice.subtotalAmount)}
                    </Typography>
                  </Box>

                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      {t("tax", "Tax")}:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatCurrency(invoice.taxAmount)}
                    </Typography>
                  </Box>

                  <Divider />

                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="h6" fontWeight="bold">
                      {t("total-amount", "Total Amount")}:
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {formatCurrency(invoice.totalAmount)}
                    </Typography>
                  </Box>

                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      {t("amount-paid", "Amount Paid")}:
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      color="success.main"
                    >
                      {formatCurrency(invoice.paidAmount)}
                    </Typography>
                  </Box>

                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      {t("remaining-amount", "Remaining Amount")}:
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      color={
                        invoice.remainingAmount > 0
                          ? "error.main"
                          : "success.main"
                      }
                    >
                      {formatCurrency(invoice.remainingAmount)}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Box>
          </PrintableContent>
        )}
      </DialogContent>

      {openCreatePayment && invoiceForActions && (
        <CreatePaymentModal
          open={openCreatePayment}
          onClose={() => setOpenCreatePayment(false)}
          invoice={invoiceForActions}
          onPaymentSuccess={
            invoiceId ? () => fetchInvoiceById({ invoiceId }) : undefined
          }
        />
      )}
    </Dialog>
  );
};

export default InvoiceDetailModal;
